import { useEffect, useState, useRef } from "react";
import { Upload, Camera, Loader2, Trash2, Eye, X, Sparkles } from "lucide-react";
import { api, fmtEur, fmtDate } from "../lib/api";
import { toast } from "sonner";

const CATEGORIE = [
    "Acquisto merce",
    "Utenze",
    "Affitto",
    "Trasporti",
    "Materiali pulizia",
    "Manutenzione",
    "Tasse",
    "Altro",
];

export default function Fatture() {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [autoExpense, setAutoExpense] = useState(true);
    const [detail, setDetail] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const fileRef = useRef(null);
    const cameraRef = useRef(null);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get("/invoices");
            setInvoices(r.data);
        } catch {
            toast.error("Errore caricamento fatture");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    async function handleFile(file) {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Carica una foto (jpg/png)");
            return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("auto_expense", autoExpense ? "true" : "false");
        try {
            const res = await api.post("/invoices/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            toast.success("Fattura analizzata!", {
                description: `${res.data.fornitore || "Fornitore"} · ${fmtEur(res.data.totale)}`,
            });
            load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Errore analisi AI";
            toast.error(msg);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
            if (cameraRef.current) cameraRef.current.value = "";
        }
    }

    async function viewDetail(id) {
        try {
            const r = await api.get(`/invoices/${id}`);
            setDetail(r.data);
            setEditForm({
                fornitore: r.data.fornitore || "",
                numero: r.data.numero || "",
                data_fattura: r.data.data_fattura || "",
                totale: r.data.totale || 0,
                imponibile: r.data.imponibile || 0,
                iva: r.data.iva || 0,
                categoria: r.data.categoria || "Altro",
                note: r.data.note || "",
            });
        } catch {
            toast.error("Errore");
        }
    }

    async function saveEdit() {
        try {
            await api.put(`/invoices/${detail.id}`, editForm);
            toast.success("Aggiornata");
            setDetail(null);
            load();
        } catch {
            toast.error("Errore salvataggio");
        }
    }

    async function delInvoice(id) {
        if (!confirm("Eliminare la fattura?")) return;
        await api.delete(`/invoices/${id}`);
        toast.success("Eliminata");
        setDetail(null);
        load();
    }

    return (
        <div className="space-y-6 pb-6" data-testid="fatture-page">
            <section className="card-soft p-6 bg-gradient-to-br from-[#FFFCF6] to-[#F4EFE0] border-[#E4DEC3]">
                <div className="flex items-start gap-4 flex-wrap">
                    <div className="w-14 h-14 rounded-2xl bg-accent-warm grid place-items-center text-white shadow-lg">
                        <Sparkles size={26} />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <h2 className="font-display text-xl font-semibold text-forest-dark">
                            Carica con AI
                        </h2>
                        <p className="text-sm text-stone-600 mt-1">
                            Foto o scansione: l'intelligenza artificiale legge fornitore, importi, IVA e crea
                            automaticamente la voce di spesa.
                        </p>
                        <label className="flex items-center gap-2 text-xs text-stone-600 mt-3">
                            <input
                                type="checkbox"
                                checked={autoExpense}
                                onChange={(e) => setAutoExpense(e.target.checked)}
                                data-testid="auto-expense-toggle"
                            />
                            Crea automaticamente la spesa in contabilità
                        </label>
                    </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={() => cameraRef.current?.click()}
                        disabled={uploading}
                        className="btn-accent inline-flex items-center gap-2 text-sm"
                        data-testid="btn-camera"
                    >
                        {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                        Scatta foto
                    </button>
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="btn-ghost inline-flex items-center gap-2 text-sm"
                        data-testid="btn-upload"
                    >
                        {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                        Carica file
                    </button>
                    <input
                        ref={cameraRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid="input-camera"
                    />
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid="input-file"
                    />
                </div>
                {uploading && (
                    <p className="text-xs text-stone-500 mt-3 italic">
                        L'AI sta analizzando il documento, può richiedere qualche secondo...
                    </p>
                )}
            </section>

            <section className="card-soft p-6" data-testid="invoices-list">
                <h2 className="font-display text-xl font-semibold text-forest-dark mb-4">
                    Fatture caricate ({invoices.length})
                </h2>
                {loading ? (
                    <div className="space-y-2">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-14 rounded-xl bg-bone-2 animate-pulse" />
                        ))}
                    </div>
                ) : !invoices.length ? (
                    <p className="text-stone-500 text-center py-10 text-sm">
                        Nessuna fattura caricata. Inizia ora!
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-soft">
                                    <th className="py-2 pr-2">Data</th>
                                    <th className="py-2 pr-2">Fornitore</th>
                                    <th className="py-2 pr-2 hidden sm:table-cell">N°</th>
                                    <th className="py-2 pr-2 hidden md:table-cell">Categoria</th>
                                    <th className="py-2 pr-2 text-right">Totale</th>
                                    <th className="py-2 text-right">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((inv) => (
                                    <tr
                                        key={inv.id}
                                        className="border-b border-soft/60 hover:bg-bone-2/50"
                                        data-testid={`invoice-row-${inv.id}`}
                                    >
                                        <td className="py-3 pr-2 text-stone-700">{fmtDate(inv.data_fattura)}</td>
                                        <td className="py-3 pr-2 font-semibold text-forest-dark">
                                            {inv.fornitore || "—"}
                                        </td>
                                        <td className="py-3 pr-2 hidden sm:table-cell text-stone-600">{inv.numero || "—"}</td>
                                        <td className="py-3 pr-2 hidden md:table-cell">
                                            <span className="chip bg-forest-50 text-forest">{inv.categoria}</span>
                                        </td>
                                        <td className="py-3 pr-2 text-right font-semibold text-forest">{fmtEur(inv.totale)}</td>
                                        <td className="py-3 text-right">
                                            <button
                                                onClick={() => viewDetail(inv.id)}
                                                className="p-2 text-forest hover:bg-forest-50 rounded-lg"
                                                data-testid={`btn-view-${inv.id}`}
                                                aria-label="Visualizza"
                                            >
                                                <Eye size={16} />
                                            </button>
                                            <button
                                                onClick={() => delInvoice(inv.id)}
                                                className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                                data-testid={`btn-del-${inv.id}`}
                                                aria-label="Elimina"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {detail && editForm && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
                    onClick={() => setDetail(null)}
                    data-testid="invoice-modal"
                >
                    <div
                        className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto animate-in slide-in-from-bottom-6 duration-300"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="sticky top-0 bg-white border-b border-soft px-6 py-4 flex items-center justify-between z-10">
                            <h3 className="font-display text-lg font-semibold text-forest-dark">
                                Dettaglio fattura
                            </h3>
                            <button
                                onClick={() => setDetail(null)}
                                className="p-2 hover:bg-bone-2 rounded-lg"
                                data-testid="modal-close"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            {detail.immagine_b64 && (
                                <img
                                    src={`data:${detail.mime_type || "image/jpeg"};base64,${detail.immagine_b64}`}
                                    alt="Fattura"
                                    className="w-full max-h-72 object-contain rounded-xl bg-bone-2 border border-soft"
                                />
                            )}
                            <div className="grid sm:grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Fornitore</span>
                                    <input
                                        className="input-soft mt-1"
                                        value={editForm.fornitore}
                                        onChange={(e) => setEditForm({ ...editForm, fornitore: e.target.value })}
                                        data-testid="edit-fornitore"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Numero</span>
                                    <input
                                        className="input-soft mt-1"
                                        value={editForm.numero}
                                        onChange={(e) => setEditForm({ ...editForm, numero: e.target.value })}
                                        data-testid="edit-numero"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Data</span>
                                    <input
                                        type="date"
                                        className="input-soft mt-1"
                                        value={editForm.data_fattura || ""}
                                        onChange={(e) => setEditForm({ ...editForm, data_fattura: e.target.value })}
                                        data-testid="edit-data"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Categoria</span>
                                    <select
                                        className="input-soft mt-1"
                                        value={editForm.categoria}
                                        onChange={(e) => setEditForm({ ...editForm, categoria: e.target.value })}
                                        data-testid="edit-categoria"
                                    >
                                        {CATEGORIE.map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Imponibile €</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="input-soft mt-1"
                                        value={editForm.imponibile}
                                        onChange={(e) => setEditForm({ ...editForm, imponibile: parseFloat(e.target.value) || 0 })}
                                        data-testid="edit-imponibile"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">IVA €</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="input-soft mt-1"
                                        value={editForm.iva}
                                        onChange={(e) => setEditForm({ ...editForm, iva: parseFloat(e.target.value) || 0 })}
                                        data-testid="edit-iva"
                                    />
                                </label>
                                <label className="block sm:col-span-2">
                                    <span className="text-xs font-semibold text-stone-600">Totale €</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="input-soft mt-1 font-semibold"
                                        value={editForm.totale}
                                        onChange={(e) => setEditForm({ ...editForm, totale: parseFloat(e.target.value) || 0 })}
                                        data-testid="edit-totale"
                                    />
                                </label>
                                <label className="block sm:col-span-2">
                                    <span className="text-xs font-semibold text-stone-600">Note</span>
                                    <textarea
                                        className="input-soft mt-1"
                                        value={editForm.note}
                                        onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                                        data-testid="edit-note"
                                    />
                                </label>
                            </div>

                            {detail.voci?.length > 0 && (
                                <div>
                                    <h4 className="font-display font-semibold text-forest-dark mb-2">
                                        Voci estratte ({detail.voci.length})
                                    </h4>
                                    <div className="max-h-48 overflow-y-auto border border-soft rounded-xl">
                                        <table className="w-full text-xs">
                                            <thead className="bg-bone-2 text-stone-500">
                                                <tr>
                                                    <th className="text-left p-2">Descrizione</th>
                                                    <th className="text-right p-2">Q.tà</th>
                                                    <th className="text-right p-2">P.U.</th>
                                                    <th className="text-right p-2">Importo</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.voci.map((v, idx) => (
                                                    <tr key={idx} className="border-t border-soft">
                                                        <td className="p-2">{v.descrizione}</td>
                                                        <td className="p-2 text-right">{v.quantita}</td>
                                                        <td className="p-2 text-right">{fmtEur(v.prezzo_unitario)}</td>
                                                        <td className="p-2 text-right font-semibold">{fmtEur(v.importo)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button onClick={saveEdit} className="btn-primary text-sm flex-1" data-testid="save-edit">
                                    Salva modifiche
                                </button>
                                <button onClick={() => delInvoice(detail.id)} className="btn-ghost text-sm" data-testid="modal-delete">
                                    <Trash2 size={14} className="inline mr-1" /> Elimina
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
